import { useTranslation } from 'react-i18next';
import { SupportedLanguage } from '../i18n/config';

export interface LocaleFormatters {
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (amount: number, currency: string) => string;
  formatRelativeTime: (date: Date | string) => string;
}

/**
 * Maps supported languages to their full locale codes
 */
const localeMap: Record<SupportedLanguage, string> = {
  en: 'en-US',
  ko: 'ko-KR'
};

/**
 * Creates locale-aware formatting functions for a specific language
 * @param locale - The supported language code (en, ko)
 * @returns Object containing formatting functions
 */
export function createLocaleFormatters(locale: SupportedLanguage): LocaleFormatters {
  const fullLocale = localeMap[locale];

  return {
    formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Handle Invalid Date
      if (isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      
      return new Intl.DateTimeFormat(fullLocale, options).format(dateObj);
    },

    formatNumber: (num: number, options?: Intl.NumberFormatOptions): string => {
      return new Intl.NumberFormat(fullLocale, options).format(num);
    },

    formatCurrency: (amount: number, currency: string): string => {
      return new Intl.NumberFormat(fullLocale, {
        style: 'currency',
        currency: currency
      }).format(amount);
    },

    formatRelativeTime: (date: Date | string): string => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const now = new Date();
      const diffInMs = now.getTime() - dateObj.getTime();
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      // Use Intl.RelativeTimeFormat for proper localization
      const rtf = new Intl.RelativeTimeFormat(fullLocale, { numeric: 'auto' });

      if (diffInYears > 0) {
        return rtf.format(-diffInYears, 'year');
      } else if (diffInMonths > 0) {
        return rtf.format(-diffInMonths, 'month');
      } else if (diffInWeeks > 0) {
        return rtf.format(-diffInWeeks, 'week');
      } else if (diffInDays > 0) {
        return rtf.format(-diffInDays, 'day');
      } else if (diffInHours > 0) {
        return rtf.format(-diffInHours, 'hour');
      } else if (diffInMinutes > 0) {
        return rtf.format(-diffInMinutes, 'minute');
      } else {
        return rtf.format(-diffInSeconds, 'second');
      }
    }
  };
}

/**
 * React hook that provides locale-aware formatting functions
 * based on the current i18n language
 * @returns Object containing formatting functions for the current locale
 */
export function useLocaleFormatters(): LocaleFormatters {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguage;
  
  return createLocaleFormatters(currentLanguage);
}
